import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { LocalStorageService } from '../core/services/data/local-storage.service';
import { UserService } from '../core/services/data/user.service';
import { UtilitiesService } from '../core/services/utilities.service';
import { MembershipUpgradeService } from '../core/services/data/membership-upgrade.service';
import { ModalService } from '../core/services/modal.service';
import { IMemberRole } from '../types/datatypes';
import { MemberRolesAPIResponse } from '../types/responses';
import { PosthogService } from '../core/services/posthog.service';

@Component({
	selector: 'app-membership',
	templateUrl: './membership.component.html',
	styleUrls: ['./membership.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class MembershipComponent implements OnInit, OnDestroy {
	public accordion = [false, false, false, false, false, false, false, false];

	public membershipFeatures = [
		{ name: 'Central Book List with Promo Reminders', level: 'free' },
		{ name: 'Saved Promo History with Easy Recheduling', level: 'free' },
		{ name: 'Author Community on Facebook', level: 'free' },
		{ name: 'Email Support', level: 'free' },
		{ name: '10% discount on all Written Word Media services', level: 'gold' },
		{ name: 'Early Access to Inventory (14 days)', level: 'gold' },
		{ name: 'Discounts on 3rd part partner products and services', level: 'gold' },
		{ name: 'Access to Limelight (dedicated email where you are the only featured author)', level: 'gold' },
		{ name: 'Access to Speaker Series Recordings', level: 'gold' },
		{ name: 'Priority Access to Beta Products', level: 'platinum' },
		{ name: 'Priority Editorial Placement (free bonus promos when we need to fill a slot!)', level: 'platinum' },
	];

	public authorLink: string;

	public memberLevel: string;

	public loading = true;

	public goldPlan: null | IMemberRole = null;

	public platinumPlan: null | IMemberRole = null;

	get isGoldDiscounted() {
		return !this.userService.isGoldMember() && this.membershipUpgradeService.goldMemberDiscount === 'true';
	}

	get goldRegularPrice() {
		if (this.loading || !this.goldPlan) {
			return 125;
		}
		return this.goldPlan.price;
	}

	get goldDiscountedPrice() {
		if (this.loading || !this.goldPlan) {
			return 100;
		}
		return this.goldPlan.priceDiscounted;
	}

	get isPlatinumDiscounted() {
		return !this.userService.isPlatinumMember() && this.membershipUpgradeService.platinumMemberDiscount === 'true';
	}

	get platinumRegularPrice() {
		if (this.loading || !this.platinumPlan) {
			return 250;
		}
		return this.platinumPlan.price;
	}

	get platinumDiscountedPrice() {
		if (this.loading || !this.platinumPlan) {
			return 200;
		}
		return this.platinumPlan.priceDiscounted;
	}

	get goldDiscountPercent() {
		if (this.loading || !this.goldPlan) {
			return 0;
		}
		return this.membershipUpgradeService.getPlanDiscountPercent(this.goldPlan);
	}

	get platinumDiscountPercent() {
		if (this.loading || !this.platinumPlan) {
			return 0;
		}
		return this.membershipUpgradeService.getPlanDiscountPercent(this.platinumPlan);
	}

	private subscriptions$ = new Subscription();

	constructor(
		private localStorageService: LocalStorageService,
		private router: Router,
		private userService: UserService,
		private utilitieService: UtilitiesService,
		private membershipUpgradeService: MembershipUpgradeService,
		private modalService: ModalService,
		private posthogService: PosthogService,
	) { }

	ngOnInit(): void {
		this.membershipUpgradeService.getPlans().subscribe((data: MemberRolesAPIResponse) => {
			/**
			 * This is bad but the design of the membership-upgrade service
			 * is tightly coupled with the user service and that doesn't fire anything
			 * for guest users, I don't want to spend any more time on this so here we go.
			 */
			if (data && data.success) {
				this.membershipUpgradeService.plans = data.roles;
			}
			this.init();
		});
		this.subscriptions$.add(this.userService.user$.subscribe((user: any) => {
			if (user) {
				this.authorLink = user.authorLink;
				this.memberLevel = user?.memberRole?.roleName?.toLowerCase();
				this.userService.fetchAuthor(this.authorLink);
			}
			this.existingMembershipIntent();
		}));

		this.posthogService.capture(this.posthogService.PRODUCT_LIST_VIEWED, { product_type: 'subscription' });
	}

	ngOnDestroy() {
		this.subscriptions$.unsubscribe();
	}

	async init() {
		/**
		 * Again, absolutely terrible but user management for Author
		 * is absolutely hideous
		 */
		this.userService.fetchUser(true).catch(() => {}).finally(() => {
			this.loading = false;
		});
		this.goldPlan = this.membershipUpgradeService._getPlanByName('gold');
		this.platinumPlan = this.membershipUpgradeService._getPlanByName('platinum');
	}

	existingMembershipIntent() {
		setTimeout(() => {
			const membershipIntent = this.localStorageService.getData('membershipIntent');
			if (membershipIntent) {
				this.localStorageService.removeData('membershipIntent');
				this.openMembershipModal(membershipIntent);
			}
		}, 1000);
	}

	toggleAccordionItem(key) {
		this.accordion[key] = !this.accordion[key];
	}

	navigateToSection(section: string): void {
		const element = document.getElementById(section);
		if (element) {
			element.scrollIntoView({ behavior: 'smooth' });
		}
	}

	gotoRegistration() {
		this.router.navigate(['/signup']);
	}

	gotoSubscriptions(level) {
		if (this.authorLink) {
			// this.router.navigate(['author-dashboard', this.authorLink, 'subscriptions', level]);
			return this.openMembershipModal(level);
		}

		this.utilitieService.saveRoute(`subscriptions/${level}`);
		this.localStorageService.saveData('membershipIntent', level);
		return this.gotoRegistration();
	}

	openMembershipModal(membershipLevel: string) {
		this.membershipUpgradeService.select(membershipLevel);
		this.modalService.open('update-card-form');
	}
}
